/**
 * Builder mode — the chat-plus-object-panel layout for building one CRM entity
 * conversationally.
 *
 * ## The mode lives in the PATH, and the conversation is the authority
 *
 * Two facts have to be true at once, and they pull in opposite directions:
 *
 * 1. **The layout needs the mode synchronously.** `DashboardLayout` decides
 *    whether to render the conversation-history nav and never loads the
 *    conversation, so it cannot wait for a fetch without flashing the nav on
 *    every builder conversation.
 * 2. **The conversation decides what it is.** `agent_type="entity_builder"` on
 *    the server is what makes a conversation a builder conversation; the client
 *    must not be able to disagree.
 *
 * So the path carries the mode (`/:orgId/build/:id`) and the conversation
 * verifies it. A path segment is part of the resource's identity — unlike the
 * `?builder=1` query param this replaces, which the conversation list, a
 * reload, and a shared link each silently dropped, taking the panel with it.
 *
 * `reconcileBuilderRoute` closes the loop: load a builder conversation at the
 * ordinary route and it redirects into `/build/:id`; load an ordinary one at
 * `/build/:id` and it redirects out. That is what makes it impossible to open a
 * builder conversation "incorrectly" — a wrong URL corrects itself rather than
 * rendering a chat with its panel silently missing.
 *
 * ## Before a conversation exists
 *
 * `/:orgId/build` is the window between "the user clicked Build" and "a
 * conversation exists". There is no conversation to consult yet, so the entity
 * type rides in `?entityType=` — the one piece of state with nowhere better to
 * live. It stops mattering the moment the conversation is created, because from
 * then on the conversation carries it.
 */

import { useLocation, useParams } from 'react-router-dom';

/** Path segment identifying a builder route. */
const BUILD_SEGMENT = 'build';

/** Entity type to build, for the pre-conversation window only. */
export const BUILDER_ENTITY_TYPE_PARAM = 'entityType';

/** Entity to edit, for the pre-conversation window only. */
export const BUILDER_ENTITY_ID_PARAM = 'entityId';

/** The agent type that marks a conversation as a builder conversation. */
export const ENTITY_BUILDER_AGENT_TYPE = 'entity_builder';

/** Path of the pre-conversation builder route for an org. */
export function builderRoutePath(orgId: number | string): string {
  return `/${orgId}/${BUILD_SEGMENT}`;
}

/** Path of an existing builder conversation. */
export function builderConversationPath(
  orgId: number | string,
  conversationId: number | string
): string {
  return `/${orgId}/${BUILD_SEGMENT}/${conversationId}`;
}

/** Path of an ordinary conversation — the destination when reconciling out. */
export function conversationPath(
  orgId: number | string,
  conversationId: number | string
): string {
  return `/${orgId}/conversations/${conversationId}`;
}

export interface UseBuilderModeResult {
  /** Whether this route renders the builder layout. */
  isBuilderMode: boolean;
  /**
   * True on `/:orgId/build` — the window before a conversation exists. Callers
   * that need "a builder conversation is loaded" must check `isBuilderMode`
   * and the conversation, not this.
   */
  isPreConversation: boolean;
}

/**
 * Read builder mode from the route.
 *
 * @example
 * ```tsx
 * const { isBuilderMode } = useBuilderMode();
 * ```
 */
export function useBuilderMode(): UseBuilderModeResult {
  const location = useLocation();
  const { conversationId } = useParams<{ conversationId?: string }>();

  // Match the segment positionally rather than with `includes`, so an org
  // slug or a conversation title containing "build" cannot turn an ordinary
  // chat into a builder one.
  const segments = location.pathname.split('/').filter(Boolean);
  const isBuilderMode = segments[1] === BUILD_SEGMENT;

  return {
    isBuilderMode,
    isPreConversation: isBuilderMode && !conversationId,
  };
}

/**
 * Where a conversation should live, given what it actually is.
 *
 * Returns the location to redirect to, or `null` when the route is already
 * right. The conversation's `agent_type` wins — the route is a rendering hint
 * that must follow it, never the other way round.
 *
 * The returned value carries `search` through deliberately. This used to
 * return a bare pathname, and the caller navigated to it with `replace: true`
 * — which erased every query param on every reconciliation, with no history
 * entry to recover them from. `?slideoutEntityId=` was the one that bit:
 * opening a builder conversation with a record slideout open silently closed
 * it. That is the same bug the nav-state effect in `ReasoningChat` already
 * guards against, and its comment names it verbatim.
 *
 * Building the full destination HERE rather than leaving callers to append
 * makes it right by construction: a future caller cannot forget.
 */
export function reconcileBuilderRoute(params: {
  orgId: number | string;
  conversationId: number | string;
  agentType: string | undefined;
  isBuilderRoute: boolean;
  /**
   * The current `location.search`, including its leading `?` (exactly what
   * `useLocation()` gives you). Optional so a caller with genuinely no query
   * string can omit it, not so it can be forgotten.
   */
  search?: string;
}): string | null {
  const { orgId, conversationId, agentType, isBuilderRoute, search } = params;

  // Unknown type — the conversation has not loaded. Redirecting on a guess
  // would bounce the user out of a correct route and back again.
  if (agentType === undefined) {
    return null;
  }

  // A lone "?" is what an emptied URLSearchParams serializes to; appending it
  // would leave a trailing "?" on the path for no reason.
  const query = search && search !== '?' ? search : '';

  const shouldBeBuilder = agentType === ENTITY_BUILDER_AGENT_TYPE;
  if (shouldBeBuilder && !isBuilderRoute) {
    return `${builderConversationPath(orgId, conversationId)}${query}`;
  }
  if (!shouldBeBuilder && isBuilderRoute) {
    return `${conversationPath(orgId, conversationId)}${query}`;
  }
  return null;
}
